import { Applicant } from '@/components/pages/Applicant/Applicant.types';
import { RequirementStatus } from './types';

const isBlank = (v: string | null | undefined): boolean => !v || v.trim() === '';

const validateAddress = (applicant: Applicant): RequirementStatus => {
    const addr = applicant.businessInfo?.address;
    if (!addr) return 'missing';
    if (
        isBlank(addr.line1) ||
        isBlank(addr.city) ||
        isBlank(addr.stateOrTerritory) ||
        isBlank(addr.zip)
    ) {
        return 'missing';
    }
    return 'valid';
};

const MIN_AGE_YEARS = 18;
const MAX_AGE_YEARS = 120;

const validateDateOfBirth = (applicant: Applicant): RequirementStatus => {
    const owners = applicant.owners ?? [];
    if (owners.length === 0) return 'missing';
    const now = Date.now();
    for (const owner of owners) {
        if (isBlank(owner.doB)) return 'missing';
        const dob = new Date(owner.doB);
        if (Number.isNaN(dob.getTime())) return 'invalid';
        const ageYears = (now - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
        if (ageYears < MIN_AGE_YEARS || ageYears > MAX_AGE_YEARS) return 'invalid';
    }
    return 'valid';
};

const validateDriversLicense = (applicant: Applicant): RequirementStatus => {
    const owners = applicant.owners ?? [];
    if (owners.length === 0) return 'missing';
    const now = Date.now();
    for (const owner of owners) {
        if (isBlank(owner.driversLicenseNumber) || isBlank(owner.driversLicenseState)) {
            return 'missing';
        }
        if (isBlank(owner.driversLicenseExpiration)) return 'missing';
        const expiry = new Date(owner.driversLicenseExpiration as string);
        if (Number.isNaN(expiry.getTime())) return 'invalid';
        if (expiry.getTime() < now) return 'expired';
    }
    return 'valid';
};

// Keyed by NmiTemplateDto.code; only seeded system templates carry a code, so reviewer-authored ones stay checklist-only.
export const REQUIREMENT_VALIDATORS: Readonly<
    Record<string, (applicant: Applicant) => RequirementStatus>
> = {
    address: validateAddress,
    dateOfBirth: validateDateOfBirth,
    driversLicense: validateDriversLicense,
};
